import { normalizePlanTier } from '../utils/planTier';

/**
 * ACCESS CONTROL CONFIGURATION
 * 
 * This is the SINGLE SOURCE OF TRUTH for all access control decisions.
 * Any time you need to check if a user can see/do something, it should
 * reference this configuration.
 * 
 * FIVE dimensions of access (checked in this order):
 * 1. Account State - Is the account active, suspended, past_due? (GATE - checked first)
 * 2. User State - Is the user active, invited, disabled? (GATE - checked second)
 * 3. Role - Who you are (owner, admin, manager, assistant, viewer)
 * 4. Tier - What the account pays for (trial/free, solo/starter, team/pro, growth/business, enterprise)
 * 5. Scope - Fine-grained permissions (data.upload, cohosts.manage, etc.)
 * 
 * PRECEDENCE RULES (deny-by-default):
 * - Account state gates override everything (suspended = no access)
 * - User state gates override role/tier (disabled = no access)
 * - If not explicitly allowed, access is DENIED
 * - Feature flags can only REDUCE access (kill switch)
 * - Tier limits are enforced server-side
 */

// ============================================================================
// ACCOUNT STATES (Gate #1 - checked before everything)
// ============================================================================
export const ACCOUNT_STATES = {
  active: {
    allowed: true,
    readOnly: false,
    description: 'Full access',
  },
  free: {
    allowed: true,
    readOnly: false,
    description: 'Post-trial permanent free tier (dashboard/ROI/upload only)',
  },
  trial: {
    allowed: true,
    readOnly: false,
    description: '30-day full access trial period',
  },
  past_due: {
    allowed: true,
    readOnly: true, // Can view but not modify
    description: 'Payment failed - read-only until resolved',
  },
  canceled: {
    allowed: true,
    readOnly: true,
    description: 'Subscription canceled - read-only access',
  },
  suspended: {
    allowed: false,
    readOnly: false,
    description: 'Account suspended - no access, contact support',
  },
  deleted: {
    allowed: false,
    readOnly: false,
    description: 'Account deleted - no access',
  },
};

// ============================================================================
// USER STATES (Gate #2 - checked after account state)
// ============================================================================
export const USER_STATES = {
  active: {
    allowed: true,
    description: 'Normal active user',
  },
  invited: {
    allowed: false, // Must accept invite first
    description: 'Pending invite acceptance',
  },
  disabled: {
    allowed: false,
    description: 'User disabled by admin',
  },
  locked: {
    allowed: false,
    description: 'User locked for security reasons',
  },
};

// ============================================================================
// SUBSCRIPTION TIERS (in order of capability)
// ============================================================================
// v5: "free" and "trial" are DISTINCT tiers
// "free" = post-trial permanent free (dashboard/ROI/upload only)
// "trial" = 30-day full access period (NO limits — full system access)
export const SUBSCRIPTION_TIERS = ['free', 'trial', 'solo', 'team', 'growth', 'enterprise', 'full_enterprise'];

// ============================================================================
// ROLE HIERARCHY (in order of capability)
// Includes future roles that are not yet exposed in UI
// ============================================================================
export const ROLE_HIERARCHY = [
  'viewer',
  'assistant', 
  'manager', 
  'admin',
  'billing_admin',  // Future: can manage billing but not delete account
  'owner',
];

// Future roles (not exposed in UI yet, but defined for forward compatibility)
export const FUTURE_ROLES = {
  billing_admin: {
    name: 'Billing Admin',
    description: 'Can manage billing but not delete account or full owner powers',
    scopes: ['billing.manage', 'analytics.view', 'finance.view', 'reports.view'],
    enabled: false, // Not exposed in UI yet
  },
  auditor: {
    name: 'Auditor',
    description: 'Read-only + export allowed, cannot edit anything',
    scopes: ['analytics.view', 'finance.view', 'reports.view', 'reports.export'],
    enabled: false,
  },
  support: {
    name: 'Internal Support',
    description: 'Internal support staff - auditable, time-bound access',
    scopes: ['*'],
    internal: true,
    enabled: false,
  },
};

/**
 * Tier-based limits
 * These define what each subscription tier allows
 */
export const TIER_LIMITS = {
  free: {
    teamSize: 1,           // Free: owner only
    vehicles: 5,           // Free: up to 5 vehicles
    cohosts: 0,            // Free: no co-hosts
    exports: true,         // PDF exports allowed
    excelExports: false,
    advancedAnalytics: false,
    apiAccess: false,
    agreementsEnabled: false,
    agreementsMonthlyLimit: 0,
    plaidSyncEnabled: false,
    stripeConnectEnabled: false,
    slackIntegrationEnabled: false,
  },
  trial: {
    teamSize: Infinity,    // Trial: FULL ACCESS — no team member limit
    vehicles: Infinity,    // Trial: FULL ACCESS — no vehicle limit
    cohosts: Infinity,     // Trial: FULL ACCESS — no co-host limit
    exports: true,
    excelExports: true,    // Trial: FULL ACCESS — all features enabled
    advancedAnalytics: true,
    apiAccess: true,       // Trial: FULL ACCESS — all features enabled
    agreementsEnabled: true,
    agreementsMonthlyLimit: Infinity, // Trial: FULL ACCESS — unlimited e-signatures
    plaidSyncEnabled: true,
    stripeConnectEnabled: true,
    slackIntegrationEnabled: true,
  },
  solo: {
    teamSize: 1, // Starter: owner only
    vehicles: 15,
    cohosts: 0,
    exports: true,
    excelExports: false,
    advancedAnalytics: true,
    apiAccess: false,
    agreementsEnabled: true,
    agreementsMonthlyLimit: 3,
    plaidSyncEnabled: true,
    stripeConnectEnabled: false,
    slackIntegrationEnabled: false,
  },
  team: {
    teamSize: 3, // Pro: owner + 2 team members
    vehicles: 40,
    cohosts: 10,
    exports: true,
    excelExports: false,
    advancedAnalytics: true,
    apiAccess: false,
    agreementsEnabled: true,
    agreementsMonthlyLimit: Infinity,
    plaidSyncEnabled: true,
    stripeConnectEnabled: true,
    slackIntegrationEnabled: true,
  },
  growth: {
    teamSize: 8, // Business: owner + 7 team members
    vehicles: 75,
    cohosts: 25,
    exports: true,
    excelExports: true,
    advancedAnalytics: true,
    apiAccess: true,
    agreementsEnabled: true,
    agreementsMonthlyLimit: Infinity,
    plaidSyncEnabled: true,
    stripeConnectEnabled: true,
    slackIntegrationEnabled: true,
  },
  enterprise: {
    teamSize: Infinity,
    vehicles: Infinity,
    cohosts: Infinity,
    exports: true,
    excelExports: true,
    advancedAnalytics: true,
    apiAccess: true,
    agreementsEnabled: true,
    agreementsMonthlyLimit: Infinity,
    plaidSyncEnabled: true,
    stripeConnectEnabled: true,
    slackIntegrationEnabled: true,
  },
  full_enterprise: {
    teamSize: Infinity,
    vehicles: Infinity,
    cohosts: Infinity,
    exports: true,
    excelExports: true,
    advancedAnalytics: true,
    apiAccess: true,
    agreementsEnabled: true,
    agreementsMonthlyLimit: Infinity,
    plaidSyncEnabled: true,
    stripeConnectEnabled: true,
    slackIntegrationEnabled: true,
  },
};

/**
 * Feature access configuration
 * Each feature defines:
 * - roles: Which roles can access (array)
 * - minTier: Minimum subscription tier required
 * - scopes: Optional fine-grained permission scopes
 * - ownerOnly: Only account owner can access
 */
export const FEATURE_ACCESS = {
  // Dashboard & Analytics
  'dashboard.view': {
    roles: ['owner', 'admin', 'manager', 'assistant', 'viewer'],
    minTier: 'free', // v5: dashboard stays free forever
  },
  'dashboard.breakdown': {
    roles: ['owner', 'admin', 'manager', 'assistant', 'viewer'],
    minTier: 'free', // v5: dashboard stays free forever
  },
  'analytics.advanced': {
    roles: ['owner', 'admin', 'manager', 'assistant', 'viewer'],
    minTier: 'trial', // v5: requires trial or paid
  },
  'analytics.view': {
    roles: ['owner', 'admin', 'manager', 'assistant', 'viewer'],
    minTier: 'trial', // v5: requires trial or paid
  },

  // Vehicle Management — v5: gated for post-trial free users
  'vehicles.view': {
    roles: ['owner', 'admin', 'manager', 'assistant', 'viewer'],
    minTier: 'trial', // v5: requires trial or paid
  },
  'vehicles.add': {
    roles: ['owner', 'admin', 'manager', 'assistant'],
    minTier: 'trial',
    scopes: ['data.upload'],
  },
  'vehicles.edit': {
    roles: ['owner', 'admin', 'manager', 'assistant'],
    minTier: 'trial',
    scopes: ['data.edit'],
  },
  'vehicles.delete': {
    roles: ['owner', 'admin', 'manager'],
    minTier: 'trial',
    scopes: ['data.delete'],
  },

  // Data Upload — v5: stays free forever
  'data.upload.earnings': {
    roles: ['owner', 'admin', 'manager', 'assistant'],
    minTier: 'free', // v5: data upload stays free forever
    scopes: ['data.upload'],
  },
  'data.upload.expenses': {
    roles: ['owner', 'admin', 'manager', 'assistant'],
    minTier: 'free', // v5: data upload stays free forever
    scopes: ['data.upload'],
  },

  // Finance Hub — v5: available during trial (gated for post-trial free)
  'finance.section': {
    roles: ['owner', 'admin'], // Only owner/admin can manage payments
    minTier: 'trial',
    scopes: ['payments.manage'],
  },
  'finance.view': {
    roles: ['owner', 'admin', 'manager', 'assistant', 'viewer'],
    minTier: 'trial', // v5: trial users get full access including finance
  },
  'finance.edit': {
    roles: ['owner', 'admin', 'manager'],
    minTier: 'trial',
  },

  // Maintenance & Expenses
  'maintenance.add': {
    roles: ['owner', 'admin', 'manager', 'assistant'],
    minTier: 'trial',
    scopes: ['data.upload'],
  },
  'maintenance.edit': {
    roles: ['owner', 'admin', 'manager', 'assistant'],
    minTier: 'trial',
    scopes: ['data.edit'],
  },
  'maintenance.delete': {
    roles: ['owner', 'admin', 'manager'],
    minTier: 'trial',
    scopes: ['data.delete'],
  },
  'expenses.add': {
    roles: ['owner', 'admin', 'manager', 'assistant'],
    minTier: 'trial',
    scopes: ['data.upload'],
  },
  'expenses.edit': {
    roles: ['owner', 'admin', 'manager', 'assistant'],
    minTier: 'trial',
    scopes: ['data.edit'],
  },
  'expenses.delete': {
    roles: ['owner', 'admin', 'manager'],
    minTier: 'trial',
    scopes: ['data.delete'],
  },

  // Incidents
  'incidents.add': {
    roles: ['owner', 'admin', 'manager', 'assistant'],
    minTier: 'trial',
    scopes: ['data.upload'],
  },
  'incidents.edit': {
    roles: ['owner', 'admin', 'manager', 'assistant'],
    minTier: 'trial',
    scopes: ['data.edit'],
  },
  'incidents.delete': {
    roles: ['owner', 'admin', 'manager'],
    minTier: 'trial',
    scopes: ['data.delete'],
  },

  // Miscellaneous Earnings
  'earnings.add': {
    roles: ['owner', 'admin', 'manager', 'assistant'],
    minTier: 'trial',
    scopes: ['data.upload'],
  },
  'earnings.edit': {
    roles: ['owner', 'admin', 'manager', 'assistant'],
    minTier: 'trial',
    scopes: ['data.edit'],
  },
  'earnings.delete': {
    roles: ['owner', 'admin', 'manager'],
    minTier: 'trial',
    scopes: ['data.delete'],
  },

  // Operations Management — v5: gated for post-trial free users
  'operations.view': {
    roles: ['owner', 'admin', 'manager', 'assistant', 'viewer'],
    minTier: 'trial', // v5: requires trial or paid
  },
  'operations.manage': {
    roles: ['owner', 'admin', 'manager'],
    minTier: 'trial',
    scopes: ['data.edit'], // operations.manage doesn't exist on backend — use data.edit
  },
  'operations.todays-focus': {
    roles: ['owner', 'admin', 'manager', 'assistant', 'viewer'],
    minTier: 'trial',
  },
  'pipeline.view': {
    roles: ['owner', 'admin', 'manager', 'assistant', 'viewer'],
    minTier: 'trial',
  },
  'pipeline.move': {
    roles: ['owner', 'admin', 'manager', 'assistant'],
    minTier: 'trial',
    scopes: ['data.edit'],
  },
  'pipeline.assign': {
    roles: ['owner', 'admin', 'manager'],
    minTier: 'trial',
    scopes: ['data.edit'],
  },

  // Cohost Management
  // Section visibility - only show Co-Hosts section to users who can manage cohosts
  // Viewers and Assistants cannot manage cohosts, so they should not see the section
  'cohosts.section': {
    roles: ['owner', 'admin', 'manager'],
    minTier: 'trial', // v5: trial users get full access including cohosts
    scopes: ['cohosts.manage'],
  },
  'cohosts.section.manage': {
    roles: ['owner', 'admin', 'manager'],
    minTier: 'trial',
    scopes: ['cohosts.manage'],
  },
  'cohosts.section.agreements': {
    roles: ['owner', 'admin'],
    minTier: 'trial',
    scopes: ['agreements.manage'],
  },
  'cohosts.view': {
    roles: ['owner', 'admin', 'manager', 'assistant', 'viewer'],
    minTier: 'trial',
  },
  'cohosts.add': {
    roles: ['owner', 'admin', 'manager'],
    minTier: 'trial',
    scopes: ['cohosts.manage'],
  },
  'cohosts.edit': {
    roles: ['owner', 'admin', 'manager'],
    minTier: 'trial',
    scopes: ['cohosts.manage'],
  },
  'cohosts.delete': {
    roles: ['owner', 'admin', 'manager'],
    minTier: 'trial',
    scopes: ['cohosts.manage'],
  },
  'cohosts.assign': {
    roles: ['owner', 'admin', 'manager'],
    minTier: 'trial',
    scopes: ['cohosts.manage'],
  },
  'cohosts.manage': {
    roles: ['owner', 'admin', 'manager'],
    minTier: 'trial',
    scopes: ['cohosts.manage'],
  },
  'cohosts.agreements': {
    roles: ['owner', 'admin'],
    minTier: 'trial',
    scopes: ['agreements.manage'],
  },
  'cohosts.payments': {
    roles: ['owner', 'admin'],
    minTier: 'trial',
    scopes: ['payments.manage'],
  },
  'cohosts.void-payments': {
    roles: ['owner', 'admin'],
    minTier: 'trial',
    scopes: ['payments.manage'],
  },
  'cohosts.starting-balance': {
    roles: ['owner', 'admin'],
    minTier: 'trial',
    scopes: ['payments.manage'],
  },

  // Reports & Exports
  'reports.view': {
    roles: ['owner', 'admin', 'manager', 'assistant', 'viewer'],
    minTier: 'trial',
  },
  'reports.share': {
    roles: ['owner', 'admin', 'manager'],
    minTier: 'trial',
  },
  // FUTURE: Export capabilities - not yet implemented server-side
  // PDF export exists client-side only (PublicReport.js) - no tier enforcement
  // CSV/Excel export not implemented
  // TODO: Implement server-side export endpoints before enabling tier enforcement
  // 'reports.export.pdf': {
  //   roles: ['owner', 'admin', 'manager', 'assistant', 'viewer'],
  //   minTier: 'solo',
  // },
  // 'reports.export.excel': {
  //   roles: ['owner', 'admin', 'manager', 'assistant'],
  //   minTier: 'team',
  // },

  // Settings — v5: always accessible (users must be able to manage their account)
  'settings.profile': {
    roles: ['owner', 'admin', 'manager', 'assistant', 'viewer'],
    minTier: 'free',
  },
  'settings.security': {
    roles: ['owner', 'admin', 'manager', 'assistant', 'viewer'],
    minTier: 'free',
  },
  'settings.business': {
    roles: ['owner', 'admin'],
    minTier: 'free',
    scopes: ['settings.manage'],
  },
  'settings.team': {
    roles: ['owner'],
    minTier: 'free',
    scopes: ['team.manage'],
    ownerOnly: true, // Only account owner can manage team members
  },
  'settings.notifications': {
    roles: ['owner', 'admin', 'manager', 'assistant', 'viewer'],
    minTier: 'free',
  },
  'settings.billing': {
    roles: ['owner'],
    minTier: 'free', // v5: must always be able to view/upgrade billing
    scopes: ['billing.manage'],
    ownerOnly: true,
  },
  'settings.advanced': {
    roles: ['owner'],
    minTier: 'free',
    ownerOnly: true,
  },
  'settings.integrations': {
    roles: ['owner', 'admin'],
    minTier: 'free', // v5: integrations page accessible (individual integrations tier-gated)
  },
  'settings.cohost-finance': {
    roles: ['owner', 'admin'],
    minTier: 'trial', // v5: trial users get full access
    scopes: ['agreements.manage'],
  },

  // Team Management
  'team.invite': {
    roles: ['owner', 'admin'],
    minTier: 'trial', // v5: trial users can invite (limit enforced by TIER_LIMITS.trial.teamSize)
    scopes: ['team.manage'],
  },
  'team.manage': {
    roles: ['owner', 'admin'],
    minTier: 'trial',
    scopes: ['team.manage'],
  },

  // Owner-only actions — v5: always accessible
  'account.delete': {
    roles: ['owner'],
    minTier: 'free',
    ownerOnly: true,
  },
  'subscription.manage': {
    roles: ['owner'],
    minTier: 'free',
    ownerOnly: true,
  },

  // Plaid Integration (Bank Sync) - Starter+
  'plaid.connect': {
    roles: ['owner', 'admin'],
    minTier: 'trial', // v5: trial users get full access including integrations
    scopes: ['settings.manage'],
  },
  'plaid.sync': {
    roles: ['owner', 'admin', 'manager'],
    minTier: 'trial',
    scopes: ['data.upload'],
  },
  'plaid.disconnect': {
    roles: ['owner', 'admin'],
    minTier: 'trial',
    scopes: ['settings.manage'],
  },

  // Stripe Connect (Automated Payouts) - Pro+
  'stripe_connect.setup': {
    roles: ['owner', 'admin'],
    minTier: 'trial', // v5: trial users get full access
    scopes: ['payments.manage'],
  },
  'stripe_connect.payouts': {
    roles: ['owner', 'admin'],
    minTier: 'trial',
    scopes: ['payments.manage'],
  },
  'stripe_connect.disconnect': {
    roles: ['owner', 'admin'],
    minTier: 'trial',
    scopes: ['payments.manage'],
  },

  // Agreements (E-Signatures) - Starter+
  'agreements.create': {
    roles: ['owner', 'admin'],
    minTier: 'trial', // v5: trial users get full access
    scopes: ['agreements.manage'],
  },
  'agreements.send': {
    roles: ['owner', 'admin'],
    minTier: 'trial',
    scopes: ['agreements.manage'],
  },
};

/**
 * UI visibility configuration
 * Defines what UI elements should show/hide based on access
 */
export const UI_VISIBILITY = {
  // Toasts & Banners
  'toast.subscription': {
    showFor: ['owner'],
    hideFor: ['admin', 'manager', 'assistant', 'viewer'],
  },
  'toast.emailVerify': {
    showFor: ['owner'],
    hideFor: ['admin', 'manager', 'assistant', 'viewer'],
  },
  'toast.businessInfo': {
    showFor: ['owner'],
    hideFor: ['admin', 'manager', 'assistant', 'viewer'],
  },
  'banner.upgrade': {
    showFor: ['owner'],
    hideFor: ['admin', 'manager', 'assistant', 'viewer'],
  },

  // Navigation items
  'nav.settings': {
    showFor: ['owner', 'admin', 'manager', 'assistant', 'viewer'],
  },
  'nav.billing': {
    showFor: ['owner'],
    hideFor: ['admin', 'manager', 'assistant', 'viewer'],
  },
};

/**
 * Helper function to check if a tier meets minimum requirement
 * 
 * NOTE: Tier restrictions are now ENABLED.
 * Backend enforcement exists via TierLimitsGuard - frontend mirrors
 * the limits but does NOT rely on them for security (server enforces).
 * Frontend tier checks provide UX hints (upgrade prompts, disabled buttons).
 */
export function tierMeetsMinimum(currentTier, minTier) {
  // Handle edge cases
  if (!currentTier || !minTier) {
    return true; // If tier info missing, let backend decide
  }

  const normalizedCurrentTier = normalizePlanTier(currentTier);
  const normalizedMinTier = normalizePlanTier(minTier);
  
  // Get tier indices
  const currentIndex = SUBSCRIPTION_TIERS.indexOf(normalizedCurrentTier);
  const minIndex = SUBSCRIPTION_TIERS.indexOf(normalizedMinTier);
  
  // If tier not found in list, fail closed to free-tier behavior
  if (currentIndex === -1) {
    return minIndex <= 0; // Can only access free-tier features
  }
  
  return currentIndex >= minIndex;
}

/**
 * Helper function to check if a role has access
 */
export function roleHasAccess(currentRole, allowedRoles) {
  return allowedRoles.includes(currentRole);
}

/**
 * Helper function to get tier limits
 */
export function getTierLimits(tier) {
  const normalizedTier = normalizePlanTier(tier);
  return TIER_LIMITS[normalizedTier] || TIER_LIMITS.free;
}

/**
 * Check account state gate (Gate #1)
 * Must pass before any other checks
 * @param {string} accountState - Account status
 * @returns {{ allowed: boolean, readOnly: boolean, reason?: string }}
 */
export function checkAccountState(accountState) {
  const state = ACCOUNT_STATES[accountState] || ACCOUNT_STATES.suspended;
  
  if (!state.allowed) {
    return { 
      allowed: false, 
      readOnly: false,
      reason: 'account_' + accountState,
      message: state.description,
    };
  }
  
  return { 
    allowed: true, 
    readOnly: state.readOnly,
  };
}

/**
 * Check user state gate (Gate #2)
 * Must pass before role/tier checks
 * @param {string} userState - User status
 * @returns {{ allowed: boolean, reason?: string }}
 */
export function checkUserState(userState) {
  const state = USER_STATES[userState] || USER_STATES.disabled;
  
  if (!state.allowed) {
    return { 
      allowed: false, 
      reason: 'user_' + userState,
      message: state.description,
    };
  }
  
  return { allowed: true };
}

/**
 * Check if user can access a feature
 * 
 * PRECEDENCE ORDER (deny-by-default):
 * 1. Account state gate (suspended/deleted = deny all)
 * 2. User state gate (disabled/locked = deny all)
 * 3. Owner-only check
 * 4. Role check
 * 5. Tier check
 * 6. Scope check
 * 7. Resource scope check (placeholder for future ABAC)
 * 
 * @param {string} feature - Feature key from FEATURE_ACCESS
 * @param {object} userContext - { role, tier, scopes, isOwner, accountState, userState }
 * @param {object} resourceContext - Optional { resourceType, resourceId, ownerId } for future ABAC
 * @returns {{ allowed: boolean, readOnly?: boolean, reason?: string }}
 */
export function checkAccess(feature, userContext, resourceContext = null) {
  const config = FEATURE_ACCESS[feature];
  
  // Deny-by-default: unknown features are denied
  if (!config) {
    console.warn(`Unknown feature: ${feature} - denying by default`);
    return { allowed: false, reason: 'unknown_feature' };
  }

  const { 
    role, 
    tier, 
    scopes = [], 
    isOwner,
    accountState = 'active',
    userState = 'active',
  } = userContext;

  // ========== GATE #1: Account State ==========
  const accountCheck = checkAccountState(accountState);
  if (!accountCheck.allowed) {
    return accountCheck;
  }

  // ========== GATE #2: User State ==========
  const userCheck = checkUserState(userState);
  if (!userCheck.allowed) {
    return userCheck;
  }

  // ========== CHECK #3: Owner-only ==========
  if (config.ownerOnly && !isOwner) {
    return { allowed: false, reason: 'owner_only' };
  }

  // ========== CHECK #4: Role ==========
  if (!roleHasAccess(role, config.roles)) {
    return { allowed: false, reason: 'role_insufficient' };
  }

  // ========== CHECK #5: Tier ==========
  if (!tierMeetsMinimum(tier, config.minTier)) {
    return { 
      allowed: false, 
      reason: 'tier_insufficient',
      requiredTier: config.minTier,
    };
  }

  // ========== CHECK #6: Scopes ==========
  if (config.scopes && config.scopes.length > 0) {
    const hasScope = config.scopes.some(s => scopes.includes(s) || scopes.includes('*'));
    if (!hasScope) {
      return { allowed: false, reason: 'scope_missing' };
    }
  }

  // ========== CHECK #7: Resource Scope (PLACEHOLDER for future ABAC) ==========
  // When implemented, this will check things like:
  // - Can user access this specific vehicle?
  // - Is user assigned to this cohost?
  // For now, we allow if all previous checks pass
  if (config.resourceScope && resourceContext) {
    // Future: implement resource-level checks
    // Examples:
    // - 'org': user can access all resources in org (default)
    // - 'assigned': user can only access resources assigned to them
    // - 'self': user can only access their own resources
    // 
    // const resourceAllowed = checkResourceScope(config.resourceScope, userContext, resourceContext);
    // if (!resourceAllowed) {
    //   return { allowed: false, reason: 'resource_scope_denied' };
    // }
  }

  // All checks passed
  return { 
    allowed: true, 
    readOnly: accountCheck.readOnly, // Pass through read-only state
  };
}

/**
 * Check if UI element should be visible
 */
export function checkUIVisibility(element, role) {
  const config = UI_VISIBILITY[element];
  
  if (!config) {
    return true; // Default to visible if not configured
  }

  if (config.hideFor && config.hideFor.includes(role)) {
    return false;
  }

  if (config.showFor && !config.showFor.includes(role)) {
    return false;
  }

  return true;
}
